import { ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Serie } from '@rossum/api-client/annotations';
import { useTheme } from '@rossum/ui/material';
import { LineChart } from '@rossum/ui/x-charts-pro';
import { LocalizationKeys } from '../../../../i18n';
import { toHumanString } from '../../../../lib/helpers';
import { dateFormatByGranularity } from '../../../../lib/timeUtils';
import GraphContainer from '../../components/GraphContainer';
import { GranularityT } from '../Granularity';

type DataKey = 'imported' | 'confirmed' | 'exported' | 'rejected' | 'deleted';
type LineDef = { key: DataKey; color: string; labelId: LocalizationKeys };

const LINE_CHART_HEIGHT = 280;

type Props = {
  isTmpState: boolean;
  series: Array<Serie>;
  children?: ReactNode;
  groupBy: GranularityT;
};

const UsageGraph = ({ isTmpState, series, children, groupBy }: Props) => {
  const intl = useIntl();
  const theme = useTheme();

  const data = useMemo(
    () =>
      series.map(
        ({
          beginDate,
          endDate,
          values: {
            importedCount,
            exportedCount,
            deletedCount,
            confirmedCount,
            rejectedCount,
          },
        }) => ({
          date: dateFormatByGranularity(
            intl.locale,
            groupBy,
            beginDate,
            endDate
          ),
          imported: importedCount || 0,
          exported: exportedCount || 0,
          deleted: deletedCount || 0,
          confirmed: confirmedCount || 0,
          rejected: rejectedCount || 0,
        })
      ),
    [series, intl.locale, groupBy]
  );

  const lines: LineDef[] = [
    {
      key: 'imported',
      color: isTmpState
        ? theme.palette.text.disabled
        : theme.palette.primary.main,
      labelId: 'components.graphs.usage.series.imported',
    },
    {
      key: 'confirmed',
      color: isTmpState ? theme.palette.grey[400] : theme.palette.warning.main,
      labelId: 'components.graphs.usage.series.confirmed',
    },
    {
      key: 'exported',
      color: isTmpState ? theme.palette.grey[500] : theme.palette.success.main,
      labelId: 'components.graphs.usage.series.exported',
    },
    {
      key: 'rejected',
      color: isTmpState ? theme.palette.grey[400] : theme.palette.error.light,
      labelId: 'components.graphs.usage.series.rejected',
    },
    {
      key: 'deleted',
      color: isTmpState ? theme.palette.grey[400] : theme.palette.error.dark,
      labelId: 'components.graphs.usage.series.deleted',
    },
  ];

  const allZero = data.every(
    d =>
      d.imported === 0 &&
      d.confirmed === 0 &&
      d.exported === 0 &&
      d.rejected === 0 &&
      d.deleted === 0
  );

  return (
    <GraphContainer fullWidth graphName="usage">
      {children}
      <LineChart
        skipAnimation
        height={LINE_CHART_HEIGHT}
        slotProps={{
          mark: {
            id: 'usage-graph-mark',
          },
        }}
        series={lines.map(line => ({
          data: data.map(d => d[line.key]),
          color: line.color,
          label: intl.formatMessage({ id: line.labelId }),
          showMark: true,
        }))}
        xAxis={[
          {
            data: data.map((_, i) => i),
            scaleType: 'band',
            valueFormatter: (v: number) => data[v]?.date ?? '',
          },
        ]}
        yAxis={[
          {
            min: 0,
            max: allZero ? 1 : undefined,
            tickNumber: 4,
            valueFormatter: toHumanString,
          },
        ]}
        grid={{ horizontal: true }}
        hideLegend
      />
    </GraphContainer>
  );
};

export default UsageGraph;
